/* eslint-disable no-underscore-dangle */
import axios from 'axios';
import config from 'config'; // Import the dynamically resolved config

console.log('Loaded config:', config);

const q = require('q');
// eslint-disable-next-line import/no-webpack-loader-syntax

const useMockAdal = config.appEnv === 'dev'; // Use mock ADAL if appEnv is 'dev'
const Adal = useMockAdal ? require('./adal-mock') : require('adal-angular');
console.log('Using ADAL:', useMockAdal ? 'Mock' : 'Real');

const adalConfig = require('./adal-config');

let _adal;

if (config.appEnv === 'dev') {
  console.log('Using mock ADAL');
  _adal = require('./adal-mock');
} else {
  console.log('Using real ADAL');
  const AuthenticationContext = require('expose-loader?AuthenticationContext!../../node_modules/adal-angular/lib/adal.js');
  _adal = new AuthenticationContext(adalConfig);
}

const _oauthData = { isAuthenticated: false, userName: '', loginError: '', profile: '' };


const updateDataFromCache = (resource) => {
  // only cache lookup here to not interrupt with events
  const token = _adal.getCachedToken(resource);
  _oauthData.isAuthenticated = token !== null && token.length > 0;
  const user = _adal.getCachedUser() || { userName: '' };
  _oauthData.userName = user.userName;
  _oauthData.profile = user.profile;
  _oauthData.loginError = _adal.getLoginError();
};

const processAdalCallback = () => {
  const hash = window.location.hash;

  if (_adal.isCallback(hash)) {
    if (window.parent && window.parent._adalInstance) {
      _adal = window.parent._adalInstance;
    }

    if (_adal._openedWindows.length > 0) {
      const lastWindow = _adal._openedWindows[_adal._openedWindows.length - 1];
      if (lastWindow.opener &&
        lastWindow.opener._adalInstance) {
        _adal = lastWindow.opener._adalInstance;
      }
    }

    // callback can come from login or iframe request
    _adal.verbose(`Processing the hash: ${hash}`);
    const requestInfo = _adal.getRequestInfo(hash);
    _adal.saveTokenFromHash(requestInfo);
    window.location.hash = '';
    // Return to callback if it is sent from iframe
    const callback = _adal._callBackMappedToRenewStates[requestInfo.stateResponse] || _adal.callback;

    if (requestInfo.stateMatch) {
      if (callback && typeof callback === 'function') {
        // Call within the same context without full page redirect keeps the callback
        if (requestInfo.requestType === _adal.REQUEST_TYPE.RENEW_TOKEN) {
          // Idtoken or Accestoken can be renewed
          if (requestInfo.parameters.access_token) {
            callback(_adal._getItem(_adal.CONSTANTS.STORAGE.ERROR_DESCRIPTION), requestInfo.parameters.access_token);

          } else if (requestInfo.parameters.id_token) {
            callback(_adal._getItem(_adal.CONSTANTS.STORAGE.ERROR_DESCRIPTION), requestInfo.parameters.id_token);

          }
        }
      } else {
        // normal full login redirect happened on the page
        updateDataFromCache(_adal.config.loginResource);
        if (_oauthData.userName) {
          // IDtoken is added as token for the app
          window.setTimeout(() => {
            updateDataFromCache(_adal.config.loginResource);
            // redirect to login requested page
            const loginStartPage = _adal._getItem(_adal.CONSTANTS.STORAGE.START_PAGE);
            if (loginStartPage) {
              window.location.path(loginStartPage);
            }
          }, 1);
        }
      }
    }
  }
};

const isAuthenticated = () => {
  const deferred = q.defer();

  console.log('Checking authentication...');
  updateDataFromCache(_adal.config.loginResource);

  // Force token acquisition in development mode
  if (config.appEnv === 'dev' || (!_adal._renewActive && !_oauthData.isAuthenticated && !_oauthData.userName)) {
    if (!_adal._getItem(_adal.CONSTANTS.STORAGE.FAILED_RENEW)) {
      console.log('Attempting to acquire token...');
      _adal.acquireToken(_adal.config.loginResource, (error, tokenOut) => {
        if (error) {
          console.error('Authentication failed:', error);
          deferred.reject();
        } else if (tokenOut) {
          console.log('Token acquired:', tokenOut);
          _oauthData.isAuthenticated = true;
          deferred.resolve();
        } else {
          console.warn('No token acquired');
          deferred.reject();
        }
      });
    } else {
      console.log('Failed renew detected, resolving...');
      deferred.resolve();
    }
  } else if (_adal._getItem(_adal.CONSTANTS.STORAGE.ERROR)) {
    console.error('Authentication error detected');
    deferred.reject();
  } else {
    console.log('User is already authenticated');
    deferred.resolve();
  }

  return deferred.promise;
};

const makeRequest = (settings) => {
  const deferred = q.defer();
  if (!settings.ignoreContentType) {
    // eslint-disable-next-line no-param-reassign
    settings.headers['Content-type'] = settings.contentType || 'application/json';
  }

  axios.request(settings).then((response) => {
    deferred.resolve(response.data);
  })
    .catch((error) => {
      if (error.response) {
        // Response has been received from the server
        deferred.reject(error.response.data); // => the response payload
      } else {
        deferred.reject(error);
      }
    });

  return deferred.promise;
};

const adalRequest = (_settings) => {
  const settings = _settings;
  const deferred = q.defer();

  console.log('Starting adalRequest with settings:', settings);

  // Ensure the user is authenticated
  isAuthenticated()
    .then(() => {
      const resource = _adal.getResourceForEndpoint(settings.url);

      if (!resource) {
        const errorMessage = `No resource configured for '${settings.url}'`;
        console.error(errorMessage);
        deferred.reject(new Error(errorMessage));
        return;
      }

      const tokenStored = _adal.getCachedToken(resource);

      if (tokenStored) {
        console.log('Token found in cache, adding to Authorization header...');
        if (!settings.headers) {
          settings.headers = {};
        }
        settings.headers.Authorization = `Bearer ${tokenStored}`;

        // Execute the request
        makeRequest(settings)
          .then((response) => {
            console.log('Request succeeded:', response);
            deferred.resolve(response);
          })
          .catch((error) => {
            console.error('Request failed:', error);
            deferred.reject(error);
          });
      } else {
        console.warn('No token found in cache, attempting to acquire token...');
        handleTokenAcquisition(resource, settings, deferred);
      }
    })
    .catch((error) => {
      console.error('Authentication failed:', error);
      if (config.appEnv !== 'dev') {
        _adal.login();
      } else {
        console.log('adalRequest: Skipping login in development mode');
        deferred.reject(error);
      }
    });

  return deferred.promise;
};

// Helper function to handle token acquisition
const handleTokenAcquisition = (resource, settings, deferred) => {
  if (_adal.loginInProgress()) {
    console.warn('Login already in progress, rejecting request...');
    deferred.reject(new Error('Login already in progress'));
    return;
  }

  _adal.acquireToken(resource, (error, tokenOut) => {
    if (error) {
      console.error('Token acquisition failed:', error);
      deferred.reject(error);
    } else if (tokenOut) {
      console.log('Token acquired successfully:', tokenOut);
      if (!settings.headers) {
        settings.headers = {};
      }
      settings.headers.Authorization = `Bearer ${tokenOut}`;

      // Execute the request
      makeRequest(settings)
        .then((response) => {
          console.log('Request succeeded after token acquisition:', response);
          deferred.resolve(response);
        })
        .catch((error) => {
          console.error('Request failed after token acquisition:', error);
          deferred.reject(error);
        });
    } else {
      console.warn('No token acquired, rejecting request...');
      deferred.reject(new Error('No token acquired'));
    }
  });
};

const logOut = () => {
  _adal.logOut();
  // call signout related method
};

const getCachedToken = () => _adal.getCachedToken(adalConfig.clientId);

const forceRedirect = () => {
  console.log('forceRedirect called');
  console.log('forceRedirect called from:', new Error().stack);
  if (config.appEnv !== 'dev') {
    _adal.clearCache();
    _adal.login();
  } else {
    console.log('Skipping redirect in development mode');
    localStorage.setItem('mock-token', 'mock-token');
    _oauthData.isAuthenticated = true;
    _oauthData.userName = 'admin';
    updateDataFromCache(_adal.config.loginResource);
  
    console.log('Simulated login complete, skipping redirect.');
    window.location.hash = '/';
  }
};



module.exports = {
  adalRequest,
  logOut: () => _adal.logOut(),
  processAdalCallback,
  oauthData: _oauthData,
  getCachedToken: () => _adal.getCachedToken(adalConfig.clientId),
  forceRedirect,
  Adal,
};



// WEBPACK FOOTER //
// ./adal/adal-request.js